/**
 * WorkshopDetailDrawer — MED-566 (E18-9)
 * 80%-width slide-out panel for workshop detail.
 * 3 tabs: Session Info, Session Results, Internal Notes.
 * Context-aware default tab: Session Info for Draft/Scheduled, Session Results for Completed.
 */

import { useState, useEffect } from 'react';
import {
  Drawer,
  Tabs,
  Badge,
  ScrollArea,
  Group,
  Text,
  Avatar,
  ActionIcon,
  Box,
  Stack,
} from '@mantine/core';
import { Calendar, Clipboard, FileText, X, Link, Clock } from 'react-feather';
import type { WorkshopWithSession } from '../../../../services/api/workshops';
import { WorkshopSessionInfoTab } from './WorkshopSessionInfoTab';
import { WorkshopSessionResultsTab } from './WorkshopSessionResultsTab';
import { InternalNotesSection } from './InternalNotesSection';
import styles from './SessionDetailDrawer.module.css';
import { formatDate, formatTime } from '../../../../utils/formatters';

function formatDuration(minutes: number | null | undefined): string {
  if (!minutes) return '';
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function getStatusBadge(status: string | null | undefined): { label: string; color: string } {
  if (!status || status === 'unscheduled') return { label: 'Draft', color: 'gray' };
  if (status === 'scheduled') return { label: 'Scheduled', color: 'blue' };
  if (status === 'in_progress') return { label: 'In Progress', color: 'dangerRed' };
  if (status === 'completed') return { label: 'Completed', color: 'medleyGreen' };
  if (status === 'cancelled') return { label: 'Cancelled', color: 'dangerRed' };
  return { label: status, color: 'gray' };
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

interface WorkshopDetailDrawerProps {
  opened: boolean;
  onClose: () => void;
  cohortId: string;
  workshop: WorkshopWithSession | null;
  sequential: boolean;
  onWorkshopUpdated?: () => void;
  /** MED-1393: false on the coach page; the resource library routes are staff-only. */
  canManageResources?: boolean;
}

export function WorkshopDetailDrawer({
  opened,
  onClose,
  cohortId,
  workshop,
  sequential,
  onWorkshopUpdated,
  canManageResources,
}: WorkshopDetailDrawerProps) {
  const [activeTab, setActiveTab] = useState<string | null>('session_info');
  const [notesCount, setNotesCount] = useState(0);

  // Context-aware default tab based on workshop status
  useEffect(() => {
    if (opened && workshop) {
      const status = workshop.session_status;
      setNotesCount(0);
      if (status === 'completed') {
        setActiveTab('session_results');
      } else {
        setActiveTab('session_info');
      }
    }
  }, [opened, workshop]);

  if (!workshop) return null;

  const statusBadge = getStatusBadge(workshop.session_status);
  const displayTitle =
    sequential && workshop.sequence_number
      ? `Workshop ${workshop.sequence_number}: ${workshop.title}`
      : workshop.title;

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="80%"
      padding={0}
      withCloseButton={false}
      styles={{
        body: { height: '100%', display: 'flex', flexDirection: 'column' },
        content: { display: 'flex', flexDirection: 'column' },
      }}
    >
      {/* Header */}
      <Box
        style={{
          borderBottom: '1px solid var(--mantine-color-gray-2)',
          padding: '16px 20px',
          flexShrink: 0,
          backgroundColor: '#fff',
        }}
      >
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
            {/* Title + status */}
            <Group gap="sm" wrap="nowrap" align="center">
              <Text fw={600} size="lg" lineClamp={1} style={{ flex: 1 }}>
                {displayTitle}
              </Text>
              <Badge
                size="sm"
                color={statusBadge.color}
                variant="light"
                style={{ textTransform: 'uppercase', letterSpacing: '0.3px', fontSize: 10, flexShrink: 0 }}
              >
                {statusBadge.label}
              </Badge>
            </Group>

            {/* Date / time / duration */}
            {workshop.scheduled_start && (
              <Group gap="xs">
                <Calendar size={13} color="#888" />
                <Text size="sm" c="dimmed">
                  {formatDate(workshop.scheduled_start)} · {formatTime(workshop.scheduled_start)}
                  {workshop.session_duration_minutes && (
                    <> · <Clock size={11} style={{ display: 'inline', verticalAlign: 'middle' }} /> {formatDuration(workshop.session_duration_minutes)}</>
                  )}
                </Text>
              </Group>
            )}

            {/* Description */}
            {workshop.description && (
              <Text size="sm" c="dimmed" lineClamp={2}>
                {workshop.description}
              </Text>
            )}

            {/* Stats bar: Facilitator | Participants | Meeting link */}
            <Group gap="md" mt={4} wrap="wrap">
              {/* Facilitator box */}
              <Box
                style={{
                  border: '1px solid #F0EEE9',
                  backgroundColor: '#F0EEE9',
                  borderRadius: 10,
                  padding: '6px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                {workshop.facilitator_id ? (
                  <>
                    <Avatar size={30} radius="xl" color="medleyGreen">
                      {getInitials(workshop.facilitator_name || 'F')}
                    </Avatar>
                    <Box>
                      <Text size="10px" fw={600} c="dimmed" style={{ textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                        Facilitator
                      </Text>
                      <Text size="13px" fw={600}>
                        {workshop.facilitator_name}
                      </Text>
                    </Box>
                  </>
                ) : (
                  <Box>
                    <Text size="10px" fw={600} c="dimmed" style={{ textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                      Facilitator
                    </Text>
                    <Text size="13px" c="dimmed">No facilitator assigned</Text>
                  </Box>
                )}
              </Box>

              {/* Participant count box */}
              <Box
                style={{
                  border: '1px solid #F0EEE9',
                  borderRadius: 10,
                  padding: '6px 16px',
                  textAlign: 'center',
                  minWidth: 80,
                }}
              >
                <Text size="16px" fw={600}>{workshop.participant_count ?? 0}</Text>
                <Text size="10px" fw={600} c="dimmed" style={{ textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                  Participants
                </Text>
              </Box>

              {/* Meeting link */}
              {workshop.zoom_link && (
                <Text
                  component="a"
                  href={workshop.zoom_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="sm"
                  c="blue"
                  style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                >
                  <Link size={13} />
                  Meeting link
                </Text>
              )}
            </Group>
          </Stack>

          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={onClose}
            aria-label="Close"
            style={{ flexShrink: 0, marginLeft: 8 }}
          >
            <X size={18} />
          </ActionIcon>
        </Group>
      </Box>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={setActiveTab}
        className={styles.tabsContainer}
      >
        <Tabs.List className={styles.tabsList}>
          <Tabs.Tab value="session_info" leftSection={<Calendar size={14} />}>
            Session Info
          </Tabs.Tab>
          <Tabs.Tab value="session_results" leftSection={<Clipboard size={14} />}>
            Session Results
          </Tabs.Tab>
          <Tabs.Tab
            value="notes"
            leftSection={<FileText size={14} />}
            rightSection={
              notesCount > 0 ? (
                <Badge size="xs" variant="filled" color="gray" circle>
                  {notesCount}
                </Badge>
              ) : undefined
            }
          >
            Internal Notes
          </Tabs.Tab>
        </Tabs.List>

        <ScrollArea className={styles.tabContent}>
          <div style={{ padding: 'var(--mantine-spacing-md)' }}>
            <Tabs.Panel value="session_info">
              <WorkshopSessionInfoTab
                key={`${workshop.id}-${workshop.facilitator_id ?? 'none'}`}
                cohortId={cohortId}
                workshopId={workshop.id}
                workshopTitle={workshop.title}
                sessionId={workshop.session_id}
                trackAttendance={workshop.attendance_tracking_enabled ?? true}
                canManageResources={canManageResources}
              />
            </Tabs.Panel>

            <Tabs.Panel value="session_results">
              {workshop.session_id ? (
                <WorkshopSessionResultsTab
                  cohortId={cohortId}
                  sessionId={workshop.session_id}
                  attendanceMarked={workshop.attendance_marked ?? false}
                  feedbackSubmitted={workshop.feedback_submitted ?? false}
                  trackAttendance={workshop.attendance_tracking_enabled ?? true}
                  onComplete={onWorkshopUpdated}
                />
              ) : (
                <Text size="sm" c="dimmed" ta="center" py="xl">
                  Schedule this workshop to access session results.
                </Text>
              )}
            </Tabs.Panel>

            <Tabs.Panel value="notes">
              {workshop.session_id ? (
                <InternalNotesSection
                  cohortId={cohortId}
                  sessionId={workshop.session_id}
                  onNotesChanged={setNotesCount}
                />
              ) : (
                <Text size="sm" c="dimmed" ta="center" py="xl">
                  Schedule this workshop to add internal notes.
                </Text>
              )}
            </Tabs.Panel>
          </div>
        </ScrollArea>
      </Tabs>
    </Drawer>
  );
}

export default WorkshopDetailDrawer;
